import clone from 'fast-clone';
import { map, isFunction, values, uniq, isEmpty, get, union } from 'lodash';
import { ROOT_NODE_ID } from '../../DynamicTree';

import {
  getNodeId,
  getNodeParentId,
} from '../store/actionHandlers/common/adapterActions';

const MAX_ROWS_INIT_EXPAND = 200;

export default class Expansion {
  constructor({
    inflateInitialExpansion = true,
    nodeShouldBeInitiallyVisible = false,
  } = {}) {
    this._inflateInitialExpansion = inflateInitialExpansion;

    this._nodeShouldBeInitiallyVisible = isFunction(
      nodeShouldBeInitiallyVisible,
    )
      ? nodeShouldBeInitiallyVisible
      : () => nodeShouldBeInitiallyVisible;
  }

  get key() {
    return 'expansion';
  }

  prepareTreeState(treeState) {
    treeState.expandedNodeIds = { [null]: true };
  }

  handleDidAddModels(
    treeState,
    models,
    { adapter, plugins: { lazyLoading, nodeSorting } },
  ) {
    let visibleModels = [];

    if (this._inflateInitialExpansion) {
      const filteredModels = models.filter(n => {
        const parentId = getNodeParentId(adapter, n);
        return parentId === null || parentId === ROOT_NODE_ID;
      });

      let checkNodes = filteredModels.map(n => getNodeId(adapter, n));

      while (
        checkNodes.length &&
        (visibleModels.length + checkNodes.length < MAX_ROWS_INIT_EXPAND ||
          visibleModels.length <= 1)
      ) {
        visibleModels.push(...checkNodes);
        checkNodes = checkNodes.reduce((acc, id) => {
          if (treeState.children[id] && treeState.children[id].length) {
            acc.push(...treeState.children[id]);
          }
          return acc;
        }, []);
      }

      visibleModels = visibleModels.map(id => treeState.models[id]);
    } else {
      visibleModels = models.filter(this._nodeShouldBeInitiallyVisible);
    }

    const expandedNodesLimit = this._inflateInitialExpansion
      ? MAX_ROWS_INIT_EXPAND
      : 0;

    let expandedNodeIds = clone(treeState.expandedNodeIds);

    if (visibleModels.length) {
      visibleModels = uniq(visibleModels);

      let sortedChildren = nodeSorting?.handleDidAddModels
        ? nodeSorting.handleDidAddModels(
            {
              children: {},
              models: visibleModels,
            },
            visibleModels,
            { adapter },
          )
        : treeState.children;

      sortedChildren = {
        ...treeState.children,
        ...sortedChildren,
      };

      const expandLazy = nodeId => {
        if (
          !expandedNodesLimit ||
          (lazyLoading?.nodeCanLoadChildren(nodeId, treeState.models) &&
            Object.keys(expandedNodeIds).length +
              get(treeState, ['models', nodeId, 'childrenCount'], 0) <
              expandedNodesLimit)
        ) {
          expandedNodeIds[nodeId] = true;
        }
      };

      let rootChildren = treeState.children[ROOT_NODE_ID];

      if (isEmpty(rootChildren)) {
        rootChildren = sortedChildren?.null || [];
      }

      try {
        rootChildren.forEach(nodeId => {
          if (
            expandedNodesLimit &&
            Object.keys(expandedNodeIds).length > expandedNodesLimit
          ) {
            throw expandedNodeIds;
          }

          expandLazy(nodeId);

          const nodeChildren = sortedChildren[nodeId] || [];

          if (
            !expandedNodesLimit ||
            nodeChildren.length + Object.keys(expandedNodeIds).length <
              expandedNodesLimit
          ) {
            expandedNodeIds = {
              ...expandedNodeIds,

              ...recursivelyCheckChildren({
                treeState,
                children: nodeChildren,
                expandedNodeIds: { [nodeId]: true, ...expandedNodeIds },
                limit: expandedNodesLimit,
                isLazyLoadedNodeStateSaved: false,
                lazyLoading,
              }),
            };
          }
        });
      } catch (result) {
        //the number of expanded nodes and their child nodes exceeds MAX_ROWS_INIT_EXPAND
        expandedNodeIds = result;
      }
    }

    treeState.expandedNodeIds = { [ROOT_NODE_ID]: true, ...expandedNodeIds };
  }

  handleDidRemoveModels(treeState, modelIdsSet) {
    if (modelIdsSet.size) {
      const expandedNodeIds = {};

      const modelIds = treeState.inputModels.map(x =>
        x.__typename ? `${x.__typename}-${x.id}` : x.id,
      );

      const mergedArray = union(
        modelIds,
        Object.keys(treeState.expandedNodeIds),
      );

      for (const key of mergedArray) {
        if (!modelIdsSet.has(key)) {
          expandedNodeIds[key] = true;
        }
      }

      treeState.expandedNodeIds = expandedNodeIds;
    }
  }

  nodeIsExpanded(treeState, nodeId) {
    return !!treeState.expandedNodeIds[nodeId];
  }

  beginChangingExpansion(treeState, nodeId, expanded, { lazyLoading }) {
    let expandedNodeIds = clone(treeState.expandedNodeIds);
    expandedNodeIds[nodeId] = expanded ? 'progress-in' : 'progress-out';

    if (expanded) {
      const children = treeState.children[nodeId];
      if (
        children &&
        children.length + values(expandedNodeIds).length < MAX_ROWS_INIT_EXPAND
      ) {
        let recursivelyExpandedNodeIds;
        try {
          recursivelyExpandedNodeIds = recursivelyCheckChildren({
            treeState,
            children,
            expandedNodeIds,
            limit: 0,
            lazyLoading,
          });
        } catch (result) {
          recursivelyExpandedNodeIds = result;
        }

        expandedNodeIds = {
          ...expandedNodeIds,
          ...recursivelyExpandedNodeIds,
        };
      }
    }

    return {
      ...treeState,
      expandedNodeIds,
    };
  }

  endChangingExpansion(treeState, nodeId, expanded, { lazyLoading }) {
    const expandedNodeIds = clone(treeState.expandedNodeIds);

    expandedNodeIds[nodeId] = !!expanded;

    if (!expanded) {
      const nodeChildren = treeState.children[nodeId];
      let shouldBeRolledUp;
      try {
        shouldBeRolledUp = recursivelyCheckChildren({
          treeState,
          children: nodeChildren,
          expandedNodeIds: {},
          limit: 0,
          lazyLoading,
        });
      } catch (result) {
        shouldBeRolledUp = result;
      }

      for (const key of Object.keys(shouldBeRolledUp)) {
        delete expandedNodeIds[key];
      }
    }

    return {
      ...treeState,
      expandedNodeIds,
    };
  }
}

const recursivelyCheckChildren = ({
  treeState,
  children,
  expandedNodeIds,
  limit = MAX_ROWS_INIT_EXPAND,
  isLazyLoadedNodeStateSaved = true,
  lazyLoading,
}) => {
  const nodeCanLazyLoadChildren = lazyLoading?.nodeCanLoadChildren;

  map(children, nodeId => {
    const nodeChildren = treeState.children[nodeId] || [];

    if (
      !limit ||
      nodeChildren.length + values(expandedNodeIds).length < limit
    ) {
      expandedNodeIds = {
        ...expandedNodeIds,
        [nodeId]:
          // skip expanding for lazyLoaded nodes
          isLazyLoadedNodeStateSaved &&
          nodeCanLazyLoadChildren &&
          nodeCanLazyLoadChildren(nodeId, treeState.models)
            ? !!treeState.expandedNodeIds.expandedNodeIds[nodeId]
            : true,
        ...recursivelyCheckChildren({
          treeState,
          children: nodeChildren,
          expandedNodeIds,
          limit,
          isLazyLoadedNodeStateSaved,
          lazyLoading,
        }),
      };
    } else {
      return expandedNodeIds;
    }
    return expandedNodeIds;
  });

  return expandedNodeIds;
};
